// Live-stream WebSocket client for the joesgrove web UI.
//
// Mirrors the contract defined by the Go backend at
// `internal/livestream/protocol.go` and the iOS counterpart at
// `ios/Packages/JoesGroveAPI/Sources/JoesGroveAPI/LiveStreamSocket.swift`.
// Anything that touches the wire — message types, payload field names,
// subprotocol — must stay aligned across all three implementations.
//
// The browser is a subscriber: it receives `live_sample` / `pen_status`
// fanout frames from the per-tenant hub and can issue `command` frames
// (e.g. `reconnect_pen`, `set_pen_stream`) back. Auth rides the
// `joesgrove_session` cookie via `credentials: include` semantics that
// WebSocket inherits for same-origin upgrades.

import { ulid } from "ulid";

/** Subprotocol identifier negotiated on the upgrade. */
export const SUBPROTOCOL = "joesgrove.v1";

/** A socket is considered dead when no inbound frame has arrived for this many
 *  ping cycles. The client pings every `pingMs`; the server pongs each ping
 *  (see ws_pen_stream.go), so on a healthy link we see a frame at least once
 *  per cycle even when the pen is silent. Silence past this window means a
 *  half-open / zombie socket (device sleep, network change, publisher dropped
 *  without a TCP FIN) — the exact state a manual page reload used to fix. */
export const STALE_PING_MULTIPLIER = 2.5;

/**
 * Pure liveness predicate, factored out so the threshold math is unit-tested
 * without standing up a socket. `lastInboundAt <= 0` means "nothing received
 * yet" (socket just opened) and is never stale.
 */
export function isStreamStale(
  lastInboundAt: number,
  now: number,
  pingMs: number,
): boolean {
  if (lastInboundAt <= 0) return false;
  return now - lastInboundAt > pingMs * STALE_PING_MULTIPLIER;
}

export type LiveStreamState =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed";

/**
 * Resolve the wall-clock "received at" a consumer should use for freshness.
 *
 * For LIVE frames this is simply `now` — skew-free, the browser's own clock.
 * For REPLAYED frames (a `resume` backfill after reconnect), `now` would brand
 * a sample captured up to 60s ago as freshly-arrived, so the UI flashes stale
 * values as "Live". Instead we use the frame's own `ts` (capture time), clamped
 * to `now` so a fast iOS clock can't make it look fresher than real time. A
 * genuinely live frame has `ts ≈ now`, so even if it were mis-flagged the
 * result is unchanged — the clamp makes this safe in both directions.
 */
export function resolveReceivedAt(
  isReplay: boolean,
  ts: string | undefined,
  now: number,
): number {
  if (isReplay && ts) {
    const parsed = Date.parse(ts);
    if (Number.isFinite(parsed)) return Math.min(now, parsed);
  }
  return now;
}

/**
 * Whether a "we're back" signal (network came online, or the tab was
 * re-focused) should revive the socket. True only when the client has GIVEN
 * UP — `state === "closed"` after exhausting the backoff ladder — and the
 * close wasn't caller-requested. A still-`reconnecting` socket has a live
 * backoff timer and is left alone (no racing dials); an `open`/`connecting`
 * one needs nothing.
 *
 * Without this, a laptop closed past the ~9-minute backoff budget lands in a
 * permanent "closed" state that only a manual page reload recovers — the very
 * thing the liveness watchdog was added to avoid. Mirrors the iOS
 * `LiveStreamSocket.handleAppWillEnterForeground` revive guard.
 */
export function shouldReviveOnWake(
  state: LiveStreamState,
  explicitClose: boolean,
): boolean {
  if (explicitClose) return false;
  return state === "closed" || state === "idle";
}

export type MsgType =
  | "hello"
  | "ping"
  | "pong"
  | "live_sample"
  | "pen_status"
  | "publisher_status"
  | "command"
  | "resume"
  | "resumed"
  | "error"
  | "bye";
// NOTE: `pen_demand` / `ack` were removed across Go / Swift / TS — demand
// rides a `set_pen_stream` command and commands are fire-and-forget, so
// neither type was ever sent.

export interface Envelope<P = unknown> {
  type: MsgType;
  id?: string;
  ts?: string;
  payload?: P;
}

export interface HelloPayload {
  tenant_id: string;
  conn_id: string;
  server_time: string;
  heartbeat_interval_ms: number;
  resume_cursor?: number | null;
}

export interface LiveSamplePayload {
  device_id?: string;
  captured_at?: string;
  ph?: number;
  ph_filtered?: number;
  electrode_mv?: number;
  ec_ms_per_cm?: number;
  temperature_c?: number;
}

export interface PenStatusPayload {
  connected: boolean;
  device_id?: string;
  rssi?: number;
  reason?: string;
}

/** Server → subscriber: whether an iOS publisher is attached to the tenant.
 *  Authoritative "app connected" signal — independent of pen/data frames. */
export interface PublisherStatusPayload {
  online: boolean;
  count: number;
}

export interface CommandPayload {
  action: "reconnect_pen" | "pause_stream" | "set_pen_stream" | string;
  target_device_id?: string;
  set_pen_stream?: { enabled: boolean; rate_hz: number };
}

export interface ErrorPayload {
  code: string;
  message: string;
  closeAfter?: boolean;
}

/** Listener types the consumer registers with `on*` methods. `isReplay` is
 *  true for frames delivered by a `resume` backfill (so consumers can date
 *  them by capture time, not arrival time — see `resolveReceivedAt`). */
type SampleListener = (
  sample: LiveSamplePayload,
  ts?: string,
  isReplay?: boolean,
) => void;
type StatusListener = (
  status: PenStatusPayload,
  ts?: string,
  isReplay?: boolean,
) => void;
type PublisherStatusListener = (
  status: PublisherStatusPayload,
  ts?: string,
) => void;
type StateListener = (state: LiveStreamState) => void;
type ErrorListener = (err: ErrorPayload) => void;

/** Constructor options. `path` defaults to the deployed endpoint. */
export interface LiveStreamClientOptions {
  /** Path relative to the current origin. Default `/v1/streams/ws`. */
  path?: string;
  /** Backoff base (ms). Capped at 30 s with full jitter. */
  baseBackoffMs?: number;
  /** Max reconnect attempts before giving up. Default 20. */
  maxAttempts?: number;
  /** Ping cadence; the server's `hello.heartbeat_interval_ms` overrides. */
  pingMs?: number;
}

/**
 * Stateful WebSocket client with auto-reconnect, ping/pong, and resume.
 *
 * Usage:
 *
 *     const client = new LiveStreamClient();
 *     client.onSample(s => setLatest(s));
 *     client.onState(s => setStatus(s));
 *     client.connect();
 *     // ...
 *     client.disconnect();
 */
export class LiveStreamClient {
  private readonly path: string;
  private readonly baseBackoffMs: number;
  private readonly maxAttempts: number;
  private pingMs: number;

  private ws: WebSocket | null = null;
  private state: LiveStreamState = "idle";
  private attempt = 0;
  private explicitClose = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastSeq = 0;
  private connId: string | null = null;
  // True while a `resume` backfill is in flight: from sending `resume` until
  // the server's `resumed` confirmation (or a short safety timeout). Frames
  // delivered in this window are replays, dated by capture time not arrival.
  private resuming = false;
  private resumeSafetyTimer: ReturnType<typeof setTimeout> | null = null;
  // Wall-clock of the last inbound frame of any type. Drives the liveness
  // watchdog (see checkLiveness); 0 until the first frame after each open.
  private lastInboundAt = 0;

  private sampleListeners = new Set<SampleListener>();
  private statusListeners = new Set<StatusListener>();
  private publisherStatusListeners = new Set<PublisherStatusListener>();
  private stateListeners = new Set<StateListener>();
  private errorListeners = new Set<ErrorListener>();

  // Bound handler for the network-online / tab-visible wake signals; non-null
  // only while listeners are attached (between connect() and disconnect()).
  private wakeHandler: (() => void) | null = null;

  constructor(opts: LiveStreamClientOptions = {}) {
    this.path = opts.path ?? "/v1/streams/ws";
    this.baseBackoffMs = opts.baseBackoffMs ?? 1000;
    this.maxAttempts = opts.maxAttempts ?? 20;
    this.pingMs = opts.pingMs ?? 15000;
  }

  /** Current connection state — also pushed to onState listeners. */
  getState(): LiveStreamState {
    return this.state;
  }

  connect(): void {
    this.explicitClose = false;
    this.attachWakeListeners();
    this.openSocket();
  }

  disconnect(): void {
    this.explicitClose = true;
    this.detachWakeListeners();
    this.endResumeWindow();
    this.stopPing();
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendEnvelope({ type: "bye" });
    }
    this.ws?.close(1000, "client_disconnect");
    this.ws = null;
    this.setState("closed");
  }

  /**
   * Revive a socket that gave up after a wake signal (network restored or tab
   * re-focused). Resets the attempt budget and re-dials immediately. Safe to
   * call any time — it no-ops unless `shouldReviveOnWake` says the socket is in
   * the give-up state. Public so a host (or a test) can trigger it directly.
   */
  wake(): void {
    if (!shouldReviveOnWake(this.state, this.explicitClose)) return;
    this.attempt = 0;
    this.openSocket();
  }

  // Listen for the browser's "we're back" signals so a permanently-closed
  // socket recovers without a page reload. `online` fires when connectivity
  // returns; `visibilitychange` fires when the user refocuses the tab (we act
  // only when it becomes visible, not when it's hidden).
  private attachWakeListeners(): void {
    if (this.wakeHandler) return;
    if (typeof window === "undefined") return;
    const handler = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }
      this.wake();
    };
    this.wakeHandler = handler;
    window.addEventListener("online", handler);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handler);
    }
  }

  private detachWakeListeners(): void {
    if (!this.wakeHandler) return;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.wakeHandler);
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.wakeHandler);
    }
    this.wakeHandler = null;
  }

  /** Send a typed command. Returns the generated envelope id. */
  sendCommand(payload: CommandPayload): string {
    const id = ulid();
    this.sendEnvelope({ type: "command", id, payload });
    return id;
  }

  onSample(fn: SampleListener): () => void {
    this.sampleListeners.add(fn);
    return () => this.sampleListeners.delete(fn);
  }

  onStatus(fn: StatusListener): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  onPublisherStatus(fn: PublisherStatusListener): () => void {
    this.publisherStatusListeners.add(fn);
    return () => this.publisherStatusListeners.delete(fn);
  }

  onState(fn: StateListener): () => void {
    this.stateListeners.add(fn);
    // Push current state immediately so subscribers don't miss the
    // initial transition.
    fn(this.state);
    return () => this.stateListeners.delete(fn);
  }

  onError(fn: ErrorListener): () => void {
    this.errorListeners.add(fn);
    return () => this.errorListeners.delete(fn);
  }

  private openSocket(): void {
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      return;
    }
    this.setState(this.attempt === 0 ? "connecting" : "reconnecting");

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}${this.path}`;
    const ws = new WebSocket(url, [SUBPROTOCOL]);
    this.ws = ws;

    ws.addEventListener("open", () => this.onOpen());
    ws.addEventListener("message", (ev) => this.onMessage(ev));
    ws.addEventListener("error", () => {
      // The `close` event always follows; do all the work there.
    });
    ws.addEventListener("close", (ev) => this.onClose(ev));
  }

  private onOpen(): void {
    this.attempt = 0;
    // A freshly opened socket hasn't received anything yet; seed the watchdog
    // clock to "now" so it doesn't immediately judge the socket stale.
    this.lastInboundAt = Date.now();
    this.setState("open");
    // If we held a `lastSeq` from a prior session, ask the server to
    // replay any frames we missed during the reconnect window. Frames that
    // arrive until the `resumed` confirmation are backfill, not live —
    // mark the window so they're dated by capture time.
    if (this.lastSeq > 0) {
      this.beginResumeWindow();
      this.sendEnvelope({
        type: "resume",
        id: ulid(),
        payload: { last_seq: this.lastSeq },
      });
    }
    this.startPing();
  }

  private beginResumeWindow(): void {
    this.resuming = true;
    if (this.resumeSafetyTimer !== null) clearTimeout(this.resumeSafetyTimer);
    // Safety net: if `resumed` never arrives (socket died mid-backfill), don't
    // strand the window open — every later live frame would otherwise be dated
    // by capture time. The replay burst lands in a tight window right after
    // `resume`, so a short timeout comfortably covers it.
    this.resumeSafetyTimer = setTimeout(() => this.endResumeWindow(), 2000);
  }

  private endResumeWindow(): void {
    this.resuming = false;
    if (this.resumeSafetyTimer !== null) {
      clearTimeout(this.resumeSafetyTimer);
      this.resumeSafetyTimer = null;
    }
  }

  private onMessage(ev: MessageEvent): void {
    // Any inbound frame — sample, status, pong, hello — is proof of life.
    this.lastInboundAt = Date.now();
    if (typeof ev.data !== "string") return;
    let env: Envelope;
    try {
      env = JSON.parse(ev.data) as Envelope;
    } catch {
      return;
    }
    switch (env.type) {
      case "hello": {
        const p = env.payload as HelloPayload;
        this.connId = p.conn_id;
        // Server may set its own preferred ping cadence — adopt it.
        if (p.heartbeat_interval_ms > 0) {
          this.pingMs = p.heartbeat_interval_ms;
          this.startPing();
        }
        if (typeof p.resume_cursor === "number") {
          // Hello tells us where the buffer is; we'll resume next reconnect.
          this.lastSeq = p.resume_cursor;
        }
        return;
      }
      case "live_sample":
        this.sampleListeners.forEach((fn) =>
          fn(env.payload as LiveSamplePayload, env.ts, this.resuming),
        );
        return;
      case "pen_status":
        this.statusListeners.forEach((fn) =>
          fn(env.payload as PenStatusPayload, env.ts, this.resuming),
        );
        return;
      case "publisher_status":
        this.publisherStatusListeners.forEach((fn) =>
          fn(env.payload as PublisherStatusPayload, env.ts),
        );
        return;
      case "pong":
        return;
      case "ping":
        // Server-initiated pings; mirror them back.
        this.sendEnvelope({ type: "pong", id: env.id });
        return;
      case "resumed": {
        // Backfill complete — subsequent frames are live again.
        this.endResumeWindow();
        const p = env.payload as { through_seq: number; replayed: number };
        if (typeof p?.through_seq === "number") {
          this.lastSeq = p.through_seq;
        }
        return;
      }
      case "error":
        this.errorListeners.forEach((fn) => fn(env.payload as ErrorPayload));
        return;
      case "bye":
        // Server is shutting down this conn; the close event will follow.
        return;
      // Anything else (server→client-only or unknown types) is ignored.
      default:
        return;
    }
  }

  private onClose(ev: CloseEvent): void {
    this.stopPing();
    this.ws = null;
    if (this.explicitClose) {
      this.setState("closed");
      return;
    }
    if (this.attempt >= this.maxAttempts) {
      this.setState("closed");
      return;
    }
    this.attempt += 1;
    const delay = this.backoff();
    this.setState("reconnecting");
    setTimeout(() => {
      if (!this.explicitClose) {
        this.openSocket();
      }
    }, delay);
    // ev unused but exposed via debug logging.
    if (ev.code && ev.code !== 1000 && typeof console !== "undefined") {
      console.debug("[livestream] closed", { code: ev.code, reason: ev.reason });
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      // Watchdog first: if the socket has gone silent it's closed here and the
      // reconnect ladder takes over, so skip pinging a dead socket.
      if (this.checkLiveness()) return;
      this.sendEnvelope({ type: "ping", id: ulid() });
    }, this.pingMs);
  }

  // Force-reconnect a socket that's stopped receiving frames. Returns true if
  // it closed the socket (the close event then runs the backoff reconnect
  // ladder, re-establishing a fresh subscription — the recovery that used to
  // require a manual page reload).
  private checkLiveness(): boolean {
    if (this.state !== "open") return false;
    if (!isStreamStale(this.lastInboundAt, Date.now(), this.pingMs)) {
      return false;
    }
    if (typeof console !== "undefined") {
      console.debug("[livestream] no inbound frames; forcing reconnect", {
        silentMs: Date.now() - this.lastInboundAt,
      });
    }
    this.ws?.close(4000, "liveness_timeout");
    return true;
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private sendEnvelope(env: Envelope): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(env));
  }

  private setState(s: LiveStreamState): void {
    if (this.state === s) return;
    this.state = s;
    this.stateListeners.forEach((fn) => fn(s));
  }

  // Capped exponential backoff with full jitter — same shape as the iOS
  // reconnect ladder so server-side connection-rate behavior is roughly
  // the same across clients.
  private backoff(): number {
    const exp = Math.min(30000, this.baseBackoffMs * 2 ** (this.attempt - 1));
    return Math.floor(Math.random() * exp);
  }
}
